'use strict';

var React = require('react');
var styled = require('styled-components');
var spacing = require('../../theme/spacing.js');

const htmlColToColSize = (col) => {
    const colKey = col.id.split('-').pop();
    if (!colKey)
        return {};
    return { [colKey]: col.style.width };
};
const colsToColSizes = (columns) => {
    const sizes = {};
    for (const column of columns) {
        sizes[column.key] = column.width || 'auto';
    }
    return sizes;
};
const MIN_COL_CONTENT_WIDTH_PX = 16;
function useResizableCols(id, columns) {
    const theme = styled.useTheme();
    const resizeRef = React.useRef({
        pageX: undefined,
        curColIdx: undefined,
        curCol: null,
        curColWidth: undefined,
        nxtCol: null,
        nxtColWidth: undefined,
    });
    const [colSize, setColSize] = React.useState(() => colsToColSizes(columns));
    const [resizing, setResizing] = React.useState(false);
    // Reset columns sizes to their default widths when the number of columns changes
    const columnsRef = React.useRef(columns);
    React.useEffect(() => {
        if (columnsRef.current.length === columns.length)
            return;
        columnsRef.current = columns;
        setColSize(colsToColSizes(columnsRef.current));
        for (const column of columnsRef.current) {
            const col = document.getElementById(`${id}-col-${column.key}`);
            if (!col)
                continue;
            col.style.width = column.width ? column.width.toString() : 'auto';
        }
    }, [columns, id]);
    const makeOnResizeMouseDown = React.useCallback((key) => (e) => {
        const target = e.target;
        const parent = target.closest('th, td');
        if (!parent)
            return;
        const nxtParent = parent.nextElementSibling;
        const curCol = document.getElementById(`${id}-col-${key}`);
        if (!curCol)
            return;
        resizeRef.current = {
            pageX: e.pageX,
            curColIdx: columns.findIndex(column => column.key === key),
            curCol,
            curColWidth: parent.offsetWidth,
            nxtCol: curCol.nextElementSibling,
            nxtColWidth: nxtParent?.offsetWidth,
        };
        setResizing(true);
    }, [id, columns]);
    React.useEffect(() => {
        const onResizeMouseMove = (e) => {
            const { designTokens: { tableCellPadding, tableFirstColumCellPaddingLeft }, } = theme;
            const resize = resizeRef.current;
            if (!resize.curCol || !resize.curColWidth || !resize.pageX)
                return;
            e.stopPropagation();
            e.preventDefault();
            const diffX = e.pageX - resize.pageX;
            const nxtColMinWidthPx = MIN_COL_CONTENT_WIDTH_PX + spacing.px(tableCellPadding) * 2;
            const firstColMinWidthPx = MIN_COL_CONTENT_WIDTH_PX + spacing.px(tableFirstColumCellPaddingLeft) + spacing.px(tableCellPadding);
            const curColMinWidthPx = resize.curColIdx === 0 ? firstColMinWidthPx : nxtColMinWidthPx;
            if (resize.curColWidth + diffX <= curColMinWidthPx ||
                (resize.nxtCol && resize.nxtColWidth && resize.nxtColWidth - diffX <= nxtColMinWidthPx)) {
                return;
            }
            resize.curCol.style.width = `${resize.curColWidth + diffX}px`;
            if (resize.nxtCol && resize.nxtColWidth) {
                resize.nxtCol.style.width = `${resize.nxtColWidth - diffX}px`;
            }
        };
        const onResizeMouseUp = () => {
            setColSize(prevColSize => {
                const isWidthChanged = resizeRef.current.curCol?.style.width;
                if (!resizeRef.current.curCol || !isWidthChanged)
                    return prevColSize;
                const curColSize = htmlColToColSize(resizeRef.current.curCol);
                const nxtColSize = resizeRef.current.nxtCol ? htmlColToColSize(resizeRef.current.nxtCol) : {};
                return { ...prevColSize, ...curColSize, ...nxtColSize };
            });
            resizeRef.current = {
                pageX: undefined,
                curColIdx: undefined,
                curCol: null,
                curColWidth: undefined,
                nxtCol: null,
                nxtColWidth: undefined,
            };
            setResizing(false);
        };
        document.addEventListener('mousemove', onResizeMouseMove);
        document.addEventListener('mouseup', onResizeMouseUp);
        return () => {
            document.removeEventListener('mousemove', onResizeMouseMove);
            document.removeEventListener('mouseup', onResizeMouseUp);
        };
    }, [theme]);
    return { colSize, makeOnResizeMouseDown, resizing };
}

exports.useResizableCols = useResizableCols;
//# sourceMappingURL=useResizableCols.js.map
